import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { TrendingUp, Upload, DollarSign, Calendar, Filter, Users, AlertTriangle, Save, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Admin: upload a weekly trips xlsx, apply flat R/parcel rate, review
 *  unmatched drivers, and view income revenue by driver with date/month filters. */
export default function DriverRevenuePage() {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Filters
  const todayIso = new Date().toISOString().slice(0, 10);
  const [filterMode, setFilterMode] = useState('month'); // 'month' | 'range'
  const [month, setMonth] = useState(todayIso.slice(0, 7));
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Data
  const [rate, setRate] = useState(0);
  const [rateDraft, setRateDraft] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [totals, setTotals] = useState({ deliveries: 0, collections: 0, total_parcels: 0, revenue: 0 });
  const [loading, setLoading] = useState(false);

  // Upload / review
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reviewUnmatched, setReviewUnmatched] = useState(null); // response from upload
  const [staffList, setStaffList] = useState([]);
  const [chosenStaff, setChosenStaff] = useState({}); // raw_name -> staff_id

  const loadRate = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/driver-revenue/rate`, { headers });
      setRate(r.data.rate || 0);
      setRateDraft(String(r.data.rate || 0));
    } catch {
      /* silent */
    }
  }, [headers]);

  const loadDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterMode === 'month'
        ? { month }
        : { date_from: dateFrom || undefined, date_to: dateTo || undefined };
      const r = await axios.get(`${API_URL}/api/driver-revenue/by-driver`, { headers, params });
      setDrivers(r.data.drivers || []);
      setTotals(r.data.totals || {});
      setRate(r.data.rate_per_parcel || rate);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load revenue');
    } finally {
      setLoading(false);
    }
  }, [headers, filterMode, month, dateFrom, dateTo, rate]);

  const loadStaff = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/driver-trips/collection-divisors`, { headers });
      setStaffList(r.data?.users || []);
    } catch {
      /* silent */
    }
  }, [headers]);

  useEffect(() => { loadRate(); loadStaff(); }, [loadRate, loadStaff]);
  useEffect(() => { loadDrivers(); }, [loadDrivers]);

  const saveRate = async () => {
    const n = parseFloat(rateDraft);
    if (!isFinite(n) || n < 0) { toast.error('Enter a positive number'); return; }
    try {
      await axios.put(`${API_URL}/api/driver-revenue/rate`, { rate: n }, { headers });
      toast.success(`Rate set to ${R(n)} / parcel`);
      setRate(n);
      loadDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rate');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Pick an .xlsx file first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      const r = await axios.post(`${API_URL}/api/driver-revenue/upload-xlsx`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${r.data.inserted} rows · ${r.data.skipped_dupe} duplicates skipped`);
      setUploadFile(null);
      const el = document.getElementById('revenue-file-input');
      if (el) el.value = '';
      if (r.data.unmatched_count > 0) {
        setReviewUnmatched(r.data);
        const pre = {};
        for (const u of r.data.unmatched) {
          if (u.suggestions?.[0]?.score >= 0.7) pre[u.raw_name] = u.suggestions[0].staff_id;
        }
        setChosenStaff(pre);
      }
      loadDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const assignOne = async (rawName) => {
    const sid = chosenStaff[rawName];
    if (!sid) { toast.error('Pick a driver to assign'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/driver-revenue/assign-driver`,
        { raw_name: rawName, staff_id: sid }, { headers });
      toast.success(`${rawName} → ${r.data.events_backfilled} events linked`);
      setReviewUnmatched(prev => ({
        ...prev,
        unmatched: prev.unmatched.filter(u => u.raw_name !== rawName),
      }));
      loadDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assign failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-emerald-500" /> Driver Revenue
          </h1>
          <p className="text-sm text-slate-500">Upload a weekly trip workbook, pay a flat rate per parcel, review by driver.</p>
        </div>

        {/* Rate + Upload */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card data-testid="revenue-rate-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-emerald-500" /> Rate per parcel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs">Flat R / parcel (applies when invoiceValue column is blank)</Label>
                  <Input type="number" min={0} step="0.01" value={rateDraft}
                    onChange={(e) => setRateDraft(e.target.value)}
                    data-testid="revenue-rate-input" />
                </div>
                <Button onClick={saveRate} data-testid="revenue-rate-save-btn" className="bg-emerald-600 hover:bg-emerald-700">
                  <Save className="h-4 w-4 mr-1" /> Save
                </Button>
              </div>
              <p className="text-[11px] text-slate-500">Current rate: <b className="text-emerald-600">{R(rate)}</b> / parcel</p>
            </CardContent>
          </Card>

          <Card data-testid="revenue-upload-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Upload className="h-4 w-4 text-cyan-500" /> Upload weekly trips (.xlsx)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input id="revenue-file-input" type="file" accept=".xlsx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="revenue-file-input"
                className="file:bg-cyan-600 file:text-white file:border-0 file:px-3 file:py-2 file:mr-2 file:cursor-pointer file:rounded-md" />
              <Button onClick={handleUpload} disabled={uploading || !uploadFile}
                className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50"
                data-testid="revenue-upload-btn">
                <Upload className="h-4 w-4 mr-1" /> {uploading ? 'Importing…' : 'Import'}
              </Button>
              <p className="text-[11px] text-slate-500">Expects two sheets: <code className="font-mono">DEL</code> (deliveries) and <code className="font-mono">COL</code> (collections). Duplicate waybills are skipped.</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1">
              <button
                onClick={() => setFilterMode('month')}
                className={`px-3 py-1.5 rounded text-sm font-semibold ${filterMode === 'month' ? 'bg-white shadow text-slate-900' : 'text-slate-600'}`}
                data-testid="filter-mode-month">
                <Calendar className="h-3 w-3 inline mr-1" /> By Month
              </button>
              <button
                onClick={() => setFilterMode('range')}
                className={`px-3 py-1.5 rounded text-sm font-semibold ${filterMode === 'range' ? 'bg-white shadow text-slate-900' : 'text-slate-600'}`}
                data-testid="filter-mode-range">
                <Calendar className="h-3 w-3 inline mr-1" /> By Date Range
              </button>
            </div>
            {filterMode === 'month' ? (
              <div>
                <Label className="text-xs">Month</Label>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                  className="w-44" data-testid="filter-month-input" />
              </div>
            ) : (
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="w-40" data-testid="filter-date-from" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="w-40" data-testid="filter-date-to" />
                </div>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={loadDrivers}
              className="ml-auto" data-testid="filter-refresh-btn">
              <RefreshCw className="h-3 w-3 mr-1" /> Refresh
            </Button>
          </CardContent>
        </Card>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white border-0" data-testid="totals-revenue-card">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase opacity-80">Total Revenue</div>
              <div className="text-xl font-bold">{R(totals.revenue)}</div>
              <div className="text-[10px] opacity-80">@ {R(rate)}/parcel</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-700 text-white border-0">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase opacity-80">Deliveries</div>
              <div className="text-xl font-bold">{totals.deliveries?.toLocaleString('en-ZA')}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-700 text-white border-0">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase opacity-80">Collections</div>
              <div className="text-xl font-bold">{totals.collections?.toLocaleString('en-ZA')}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-700 to-slate-900 text-white border-0">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase opacity-80">Total Parcels</div>
              <div className="text-xl font-bold">{totals.total_parcels?.toLocaleString('en-ZA')}</div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" /> Revenue by driver <span className="text-xs text-slate-500 font-normal">({drivers.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="rounded border max-h-[65vh] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead>Driver</TableHead>
                    <TableHead className="text-center">Working days</TableHead>
                    <TableHead className="text-right">Deliveries</TableHead>
                    <TableHead className="text-right">Collections</TableHead>
                    <TableHead className="text-right">Total parcels</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading
                    ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>
                    : drivers.length === 0
                      ? <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400 italic">No trip data yet — upload a weekly workbook above.</TableCell></TableRow>
                      : drivers.map((d) => (
                        <TableRow key={d.staff_id || 'unassigned'} className={d.staff_id ? '' : 'bg-amber-50/50'}
                          data-testid={`revenue-row-${d.staff_id || 'unassigned'}`}>
                          <TableCell>
                            <div className="font-semibold">{d.name}</div>
                            {!d.staff_id && (
                              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] gap-1 mt-1">
                                <AlertTriangle className="h-3 w-3" /> Needs mapping
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center text-slate-600">{d.working_days}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-700">{d.deliveries.toLocaleString('en-ZA')}</TableCell>
                          <TableCell className="text-right font-mono text-indigo-700">{d.collections.toLocaleString('en-ZA')}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{d.total_parcels.toLocaleString('en-ZA')}</TableCell>
                          <TableCell className="text-right font-mono font-bold text-emerald-700">{R(d.revenue)}</TableCell>
                        </TableRow>
                      ))}
                </TableBody>
                {drivers.length > 0 && (
                  <tfoot className="bg-slate-900 text-white font-bold">
                    <tr>
                      <td className="px-4 py-2">TOTAL</td>
                      <td />
                      <td className="text-right px-4 py-2">{totals.deliveries?.toLocaleString('en-ZA')}</td>
                      <td className="text-right px-4 py-2">{totals.collections?.toLocaleString('en-ZA')}</td>
                      <td className="text-right px-4 py-2">{totals.total_parcels?.toLocaleString('en-ZA')}</td>
                      <td className="text-right px-4 py-2 text-emerald-300" data-testid="revenue-total-cell">{R(totals.revenue)}</td>
                    </tr>
                  </tfoot>
                )}
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Review unmatched dialog */}
        <Dialog open={!!reviewUnmatched && reviewUnmatched.unmatched?.length > 0} onOpenChange={(o) => !o && setReviewUnmatched(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="revenue-review-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" /> {reviewUnmatched?.unmatched_count} driver name{reviewUnmatched?.unmatched_count === 1 ? '' : 's'} need mapping
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 overflow-y-auto max-h-[60vh]">
              {reviewUnmatched?.unmatched?.map((u) => (
                <div key={u.raw_name} className="rounded border p-3 flex flex-col sm:flex-row sm:items-center gap-2"
                  data-testid={`unmatched-row-${u.raw_name.replace(/\s+/g, '-')}`}>
                  <div className="flex-1">
                    <div className="font-semibold">{u.raw_name}</div>
                    <div className="text-[10px] text-slate-500">
                      {u.count} rows · {u.plate ? `vehicle ${u.plate}` : 'no plate'}
                      {u.suggestions?.[0] && ` · best match ${(u.suggestions[0].score * 100).toFixed(0)}%`}
                    </div>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Select value={chosenStaff[u.raw_name] || ''} onValueChange={(v) => setChosenStaff(prev => ({ ...prev, [u.raw_name]: v }))}>
                      <SelectTrigger className="w-full sm:w-56" data-testid={`unmatched-select-${u.raw_name.replace(/\s+/g, '-')}`}>
                        <SelectValue placeholder="Pick staff…" />
                      </SelectTrigger>
                      <SelectContent>
                        {u.suggestions?.map((s) => (
                          <SelectItem key={`sg-${s.staff_id}`} value={s.staff_id}>
                            {s.full_name} <span className="opacity-50 text-xs">({(s.score * 100).toFixed(0)}%)</span>
                          </SelectItem>
                        ))}
                        {u.suggestions?.length > 0 && <div className="border-t my-1" />}
                        {staffList.map((s) => (
                          <SelectItem key={`all-${s.id}`} value={s.id}>{s.full_name || s.username}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={() => assignOne(u.raw_name)}
                      disabled={!chosenStaff[u.raw_name]}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
                      data-testid={`unmatched-assign-${u.raw_name.replace(/\s+/g, '-')}`}>
                      Assign
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewUnmatched(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
